import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import './device-page.styles.scss';

import HouseComposition from './components/house-composition-component/house-composition.component';
import DeviceGeneralStatus from './components/device-general-status-component/device-general-status.component';
import DeviceIOSettings from './components/device-io-settings-component/device-io-settings.component';
import DeviceDetails from './components/device-details-component/device-details.component';
import DeviceDiagnostics from './components/device-diagnostics-component';
import DeviceNavMenuOptions from './components/device-nav-menu-option-component/device-nav-menu-option.component';
import Tools from './components/tools';
import WifiIcon from '@lifepowr/components/src/assets/icons/device-house-wifi.png';
import NoWifiIcon from '@lifepowr/components/src/assets/icons/device-house-no-wifi.png';

import validator from '@rjsf/validator-ajv8';
import Form from '@rjsf/mui';

import EanPic from '@lifepowr/components/src/assets/images/ean.png';
import balancingSchema from './components/device-details-component/balancingSchema';

import * as systemCreators from '@lifepowr/components/src/store/system';
import { connect } from 'react-redux';
import { API } from 'Amplify';

import getClientInfo from '@lifepowr/components/src/clients';
import { useDeviceDataApiHook } from './components/shared/device-data-api.hook';
import { ApiClientStatusEnum } from '@lifepowr/components/src/utils/api-client';
import CircularProgressLoader from '../../components-shared/circular-progress-loader-component/circular-progress-loader.component';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

const { default: _df, ...systemActionCreators } = systemCreators;

/**
 * Retuned components according to menu selection
 */
const DeviceBody = ({ value, deviceId, userMode, admin }) => {
  switch (value) {
    case 2:
      return userMode ? null : <DeviceIOSettings deviceId={deviceId} />;

    case 3:
      return (
        <DeviceDetails
          userMode={userMode}
          admin={admin}
          deviceId={deviceId}
        />
      );

    case 4:
      return userMode ? null : (
        <DeviceDiagnostics
          admin={admin}
          userMode={userMode}
          deviceId={deviceId}
        />
      );

    default:
      return (
        <DeviceGeneralStatus
          userMode={userMode}
          deviceId={deviceId}
          admin={admin}
        />
      );
  }
};

/**
 * This component renders the Device Details page and coordinates the presentation of other components.
 * @param {*} props
 * @returns
 */
const DeviceContent = (props) => {
  const {
    connected, //
    system,
    userMode = false,
    diagnostics = {},
    balancing,
    adminStore,
  } = props;
  const { attributes: { name: systemName = 'Unnamed system' } = {}, thingTypeName, shadow } = system || {};
  const { admin } = adminStore;

  const { deviceName: thingName, pathName, batteryPathName, fleetOption } = useParams();
  const { ean: eanBalancing } = balancing || {};

  const error = false;
  const [loadingName, setLoadingName] = useState(false);
  const [editName, setEditName] = useState(false);
  const [currentName, setCurrentName] = useState(systemName);
  const [errorName, setErrorName] = useState('');
  const [thingStatus, setThingStatus] = useState(true);
  const [eanFormData, setEanFormData] = useState({ ean: eanBalancing });
  const [eanOpen, setEanOpen] = useState(false);
  const [eanLoading, setEanLoading] = useState(false);
  const [eanError, setEanError] = useState(null);

  const { search } = useLocation();
  const { setThingName, triggerDeviceData, deviceDataApiClientStatus } = useDeviceDataApiHook();

  let siteConnected = connected || false;
  if (typeof thingTypeName === 'string' && thingTypeName.startsWith('lp_virtual')) {
    const { metadata: { diagnostics: siteDiagnostics = {} } = {} } = shadow;
    const metadataTimestamp = siteDiagnostics?.reported?.cloudFlex?.status?.timestamp || 0;
    const now = Date.now();
    const diff = now - metadataTimestamp * 1000; // Convert to milliseconds
    siteConnected = diff < 15 * 60 * 1000; // 15 minutes cutoff to disconnect
  }

  useEffect(() => {
    const clientInfo = getClientInfo();

    const showIbanFederated = clientInfo ? clientInfo?.iban : true;
    setEanOpen(!eanBalancing && showIbanFederated);
  }, [balancing?.ean, eanBalancing, balancing]);

  /** MAIN menu options */
  const variable = [
    { id: 0, uri: 'general-status', name: 'General Status' },
    { id: 3, uri: 'device-details', name: 'Device Details' },
  ];

  if (!userMode) {
    variable.splice(1, 0, { id: 2, uri: 'io-settings', name: 'IO Settings' });
    variable.splice(2, 0, { id: 4, uri: 'io-diagnostics', name: 'IO Diagnostics', warning: !thingStatus });
  }

  const [active, setActive] = useState(() => variable.find((item) => item.uri.includes(pathName))?.id);

  useEffect(() => {
    if (diagnostics) {
      const result = Object.entries(diagnostics || {}).reduce((acc, [_, value]) => {
        const { status } = value || {};

        return status !== undefined ? status && acc : acc;
      }, true);
      setThingStatus(result);
    }
  }, [diagnostics]);

  useEffect(() => {
    if (!window.document.title.includes(systemName) && systemName !== 'Unnamed system') {
      window.document.title = `${window.document.title} ${systemName}`;
    }
  }, [systemName]);

  useEffect(() => {
    setCurrentName(systemName.split('_').join(' '));
  }, [systemName]);

  useEffect(() => {
    setThingName(thingName);
  }, [thingName]);

  const loading = deviceDataApiClientStatus.clientStatus === ApiClientStatusEnum.Busy;
  const errorLoadingDevice = deviceDataApiClientStatus.clientStatus === ApiClientStatusEnum.Error;

  const submitName = async () => {
    setLoadingName(true);
    setErrorName('');
    try {
      const body = { name: currentName };
      await API.post('IO_API', `/devices/${thingName}/name`, { body });
      setEditName(false);
    } catch (e) {
      setErrorName(`${e}`);
    }
    setLoadingName(false);
  };

  const nameButtonDisable = currentName === systemName.split('_').join(' ') || !currentName || loadingName;

  async function onFormSubmit(ev) {
    setEanLoading(true);
    setEanError(null);
    try {
      const { formData: body } = ev;
      const res = await API.post('IO_API', `/devices/${thingName}/balancing`, { body });
      console.log(res);
    } catch (e) {
      const { response: { data } = {} } = e;
      setEanError(data || e);
    }
    setEanLoading(false);
  }

  function onFormChange(ev) {
    const { formData } = ev;
    setEanFormData(formData);
  }

  const genericFailureMemo = useMemo(() => {
    return (
      <>
        <AlertTitle>Failed to load device information!</AlertTitle>

        <Typography paragraph>If you continue experiencing this issue, please contact technical support for assistance.</Typography>

        <Button onClick={triggerDeviceData}>Retry</Button>
      </>
    );
  }, [thingName]);

  const forbiddenAccessMemo = useMemo(() => {
    return (
      <>
        <AlertTitle>Not permitted to access device!</AlertTitle>

        <Typography paragraph>
          You don't have permission to access this device. If it's a new device it may take a few minutes to propagate the correct permissions.
          <br />
          Please select a valid device from the devices list.
        </Typography>

        <Link to={`/${fleetOption}/devices`}>
          <Button>Go to device list</Button>
        </Link>
      </>
    );
  }, [thingName]);

  const notFoundMemo = useMemo(() => {
    return (
      <>
        <AlertTitle>Device doesn't exist!</AlertTitle>

        <Typography paragraph>
          The device does not exist.
          <br />
          Please select a valid device from the devices list.
        </Typography>

        <Link to={`/${fleetOption}/devices`}>
          <Button>Go to device list</Button>
        </Link>
      </>
    );
  }, [thingName]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center' }}>
        <CircularProgressLoader />
        <span>Loading device data...</span>
      </div>
    );
  }

  if (errorLoadingDevice) {
    let memo = genericFailureMemo;

    const errorForbiddenAccess = deviceDataApiClientStatus.error?.response?.status === 403;
    const errorDeviceNotFound = deviceDataApiClientStatus.error?.response?.status === 404;

    if (errorForbiddenAccess) {
      memo = forbiddenAccessMemo;
    } else if (errorDeviceNotFound) {
      memo = notFoundMemo;
    }

    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Alert
          severity="error"
          sx={{ mb: 2, mt: 2, mx: 0.5, boxShadow: 2, minWidth: 700 }}
        >
          {memo}
        </Alert>
      </div>
    );
  }

  return (
    <>
      <Dialog
        open={Boolean(editName)}
        onClose={() => setEditName(false)}
      >
        {errorName && <div className="requestError">Error: {errorName}</div>}

        <div>
          <TextField
            label="Device Name"
            value={currentName}
            onChange={(ev) => {
              setCurrentName(ev.target.value);
            }}
          />

          <IconButton
            disabled={nameButtonDisable}
            onClick={submitName}
            size="large"
          >
            {loadingName ? <CircularProgress /> : <CheckIcon />}
          </IconButton>
        </div>
      </Dialog>

      <Dialog
        open={userMode && eanOpen}
        disablePortal={true}
      >
        <DialogTitle>To join the revolution, provide your EAN</DialogTitle>

        <DialogContent>
          <div>So your FlexBox can participate in balancing markets, we require your meter EAN. You can find it:</div>
          <img
            src={EanPic}
            alt="Logo"
            className="eanPic"
          />
          <ol>
            <li>On all invoices from your energy supplier</li>
            <li>On the quote for a new connection</li>
            <li>On the sticker on your meter box</li>
          </ol>

          <Form
            schema={balancingSchema}
            validator={validator}
            onSubmit={onFormSubmit}
            onChange={onFormChange}
            formData={eanFormData}
            extraErrors={eanError ? { Error: { __errors: [eanError] } } : {}}
            disabled={eanLoading}
          >
            <Stack
              spacing={2}
              direction="row"
            >
              <Button
                disabled={eanLoading}
                type="submit"
                variant="contained"
              >
                {eanLoading ? <CircularProgress /> : 'Submit'}
              </Button>
            </Stack>
          </Form>
        </DialogContent>
      </Dialog>

      {error ? <div>Error: {error}</div> : null}

      <div className="device-page">
        <div className="device-page__left">
          <div className="composition_wrapper">
            <Typography
              variant="h5"
              className="device-page__title"
            >
              <img
                src={siteConnected ? WifiIcon : NoWifiIcon}
                alt="connectivity-icon"
              />
              {` ${systemName.split('_').join(' ')}`}

              <IconButton
                size="large"
                onClick={() => {
                  setEditName(true);
                }}
              >
                <EditIcon />
              </IconButton>
            </Typography>
            <HouseComposition />
          </div>

          <div className={`device-page__console ${!connected ? 'device-page__console__notSelected' : ''}`}>{!userMode ? <Tools admin={admin} /> : null}</div>
        </div>

        <div className="device-page__right">
          <div className="device-nav">
            <div className="device-nav-menu">
              {variable?.map(({ name, id, uri, warning }, index) => (
                <div key={`device-nav-menu_${index}`}>
                  <Link to={batteryPathName ? `../${uri}${search}` : `./${uri}${search} `}>
                    <DeviceNavMenuOptions
                      {...{ id, name, warning }}
                      isActive={active === id}
                      handleClick={() => setActive(id)}
                      showUnderline
                    />
                  </Link>
                </div>
              ))}
            </div>
          </div>

          <div className="device-body">
            <DeviceBody
              userMode={userMode}
              value={active}
              admin={admin}
              deviceId={thingName}
            />
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProp = (state) => {
  const adminStore = state?.admin;
  const system = state?.system || {};
  const { connected, code, shadow } = system;
  const { reported, desired } = shadow || {};
  const { diagnostics } = reported || {};
  const { balancing } = desired || {};
  return { connected, system, code, diagnostics, adminStore, balancing };
};

export default connect(mapStateToProp, { ...systemActionCreators })(DeviceContent);
